/**
 * API服务 - 与后端 FastAPI 交互
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 允许发送和接收 cookies
});

// ============ 认证相关 ============

/**
 * 用户登录
 * @param {Object} data - { account, password, captcha_code }
 */
export const login = async (data) => {
  const response = await apiClient.post('/auth/login', data);
  return response.data;
};

/**
 * 用户登出
 */
export const logout = async () => {
  const response = await apiClient.post('/auth/logout');
  return response.data;
};

/**
 * 检查登录状态
 */
export const checkAuth = async () => {
  const response = await apiClient.get('/auth/check');
  return response.data;
};

/**
 * 获取验证码图片
 */
export const getCaptcha = async () => {
  const response = await apiClient.get('/auth/captcha');
  return response.data;
};

/**
 * 修改密码
 * @param {Object} data - { old_password, new_password }
 */
export const changePassword = async (data) => {
  const response = await apiClient.put('/auth/password', data);
  return response.data;
};

/**
 * 获取省市区数据
 */
export const getRegions = async () => {
  const response = await apiClient.get('/regions');
  return response.data;
};

/**
 * 获取街道列表（按需加载）
 * @param {string} province - 省份
 * @param {string} city - 城市
 * @param {string} district - 区县
 */
export const getStreets = async (province, city, district) => {
  const response = await apiClient.get('/streets', {
    params: { province, city, district }
  });
  return response.data;
};

/**
 * 获取门店类型（两级级联）
 */
export const getStoreTypes = async () => {
  const response = await apiClient.get('/store-types');
  return response.data;
};

/**
 * 获取门店数据（支持筛选）
 * @param {Object} filters - 筛选条件
 * @param {Array} filters.provinces - 省份列表
 * @param {Array} filters.cities - 城市列表
 * @param {Array} filters.districts - 区县列表
 * @param {Array} filters.parentTypes - 父级门店类型
 * @param {Array} filters.childTypes - 子级门店类型
 */
export const getStores = async (filters) => {
  const response = await apiClient.post('/stores', filters);
  return response.data;
};

/**
 * 获取行政区边界数据
 * @param {Object} filters - 筛选条件
 * @param {Array} filters.provinces - 省份列表
 * @param {Array} filters.cities - 城市列表
 * @param {Array} filters.districts - 区县列表
 */
export const getBoundaries = async (filters) => {
  const response = await apiClient.post('/boundaries', filters);
  return response.data;
};

/**
 * 健康检查
 */
export const healthCheck = async () => {
  const response = await apiClient.get('/health');
  return response.data;
};

/**
 * 获取品类列表（一级+二级级联）
 */
export const getCategories = async () => {
  const response = await apiClient.get('/categories');
  return response.data;
};

/**
 * 获取区域品类需求数据（带归一化指数）
 * @param {Object} filters - 筛选条件
 * @param {string} filters.province - 省份名
 * @param {string} filters.city - 城市名
 * @param {string} filters.district - 区县名
 * @param {string} filters.street - 街道名
 * @param {string} filters.first_category - 一级品类
 * @param {string} filters.second_category - 二级品类
 */
export const getDemandData = async (filters) => {
  const response = await apiClient.post('/demand-data', filters);
  return response.data;
};

/**
 * 获取区域人口年龄结构数据（带归一化指数）
 * @param {Object} filters - 筛选条件
 * @param {string} filters.province - 省份名
 * @param {string} filters.city - 城市名
 * @param {string} filters.district - 区县名
 * @param {Array} filters.population_types - 人口类型数组 ['youth', 'labor', 'elderly']
 */
export const getPopulationData = async (filters) => {
  const response = await apiClient.post('/population-data', filters);
  return response.data;
};

/**
 * 获取区域收入层级数据（带归一化指数）
 * @param {Object} filters - 筛选条件
 * @param {string} filters.province - 省份名
 * @param {string} filters.city - 城市名
 * @param {string} filters.district - 区县名
 * @param {string} filters.income_level - 收入层级 'high' | 'middle' | 'low'
 */
export const getIncomeData = async (filters) => {
  const response = await apiClient.post('/income-data', filters);
  return response.data;
};

const apiService = {
  login,
  logout,
  checkAuth,
  getCaptcha,
  changePassword,
  getRegions,
  getStreets,
  getStoreTypes,
  getStores,
  getBoundaries,
  healthCheck,
  getCategories,
  getDemandData,
  getPopulationData,
  getIncomeData,
};

export default apiService;
